// ═══════════════════════════════════════════════════════════════════
//  modules/wiki/WikiPage.js
//  WikiPage
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { wCat } from "./constantes";
import { wBtnSm, wGhostSm, wLbl, wPanel } from "./styles";
import { WikiContenu } from "./WikiContenu";

// ═══════════════ Page en lecture ═══════════════
export function WikiPage({ api, slug, onRetour, onOuvrir, onModifier, onHistorique, onChip, token }) {
  const isMobile = useIsMobile();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [coherence, setCoherence] = useState(null);
  const [analyse, setAnalyse] = useState(false);
  const [detailLecteurs, setDetailLecteurs] = useState(false);

  const charger = useCallback(() => api.page(slug).then(setD).catch((e) => setErreur(e.message)), [api, slug]);
  useEffect(() => { setD(null); setErreur(""); setCoherence(null); charger(); }, [slug, charger]);

  const verifierCoherence = async () => {
    setAnalyse(true);
    try { setCoherence(await api.ia("coherence", { pageId: d.page.id })); }
    catch (e) { setCoherence({ erreur: e.message }); }
    setAnalyse(false);
  };
  const changerStatut = async (statut) => { await api.statut(d.page.id, statut); charger(); };
  const supprimer = async () => {
    if (!window.confirm("Mettre cette page à la corbeille ?")) return;
    await api.supprimer(d.page.id); onRetour();
  };
  const dupliquer = async () => {
    const titre = window.prompt("Titre de la copie :", d.page.titre.replace(/^Gabarit — /, "") + " (copie)");
    if (titre === null) return;
    try { const r = await api.dupliquer(d.page.id, { titre }); onOuvrir(r.slug); }
    catch (e) { setErreur(e.message); }
  };
  const marquerLu = async () => {
    try { const r = await api.marquerLu(d.page.id); setD((x) => ({ ...x, lecture: r.lecture })); }
    catch (e) { setErreur(e.message); }
  };
  const imprimer = () => {
    window.open(`${API_BASE}/api/wiki/pages/${d.page.slug}/imprimer?jeton=${d.jetonFichiers}`, "_blank", "noopener");
  };

  if (erreur) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <button onClick={onRetour} style={{ ...wGhostSm, alignSelf: "flex-start" }}>← Wiki</button>
      <div style={{ ...wPanel, color: THEME.red }}>{erreur}</div>
    </div>
  );
  if (!d) return <div style={{ color: THEME.textMuted }}>Chargement…</div>;

  const { page, html, toc, fichiers, liens, entrants, droits } = d;
  const st = wCat(page.categorie);
  // Les pastilles emportent la page d'origine : le module d'arrivée peut proposer le retour
  const chip = (t, i, l) => onChip(t, i, l, page);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button onClick={onRetour} style={wGhostSm}>← Wiki</button>
        {/* ETOILE_FAVORI — marque-page personnel. À ne pas confondre avec
            l'épinglage, qui est une décision d'admin pour toute l'équipe :
            celle-ci ne regarde que toi et personne ne la voit. */}
        <button onClick={async () => {
            const r = await api.favori(page.id);
            setD((x) => ({ ...x, page: { ...x.page, favori: r.favori ? 1 : 0 } }));
          }}
          style={Number(page.favori)
            ? wBtnSm(THEME.amberDim, THEME.amber, THEME.amber)
            : wGhostSm}
          title={Number(page.favori)
            ? "Retirer de mes favoris"
            : "Garder cette fiche sous la main — visible de toi seul"}>
          {Number(page.favori) ? "⭐" : "☆"}
        </button>
        <div style={{ flex: 1 }} />
        {droits.modifier && <button onClick={() => onModifier(page)} style={wBtnSm(THEME.accentDim, THEME.accent, THEME.accent)}>✏️ Modifier</button>}
        <button onClick={() => onHistorique(page)} style={wGhostSm}>🕘 Historique v{page.version}</button>
        {droits.publier && page.statut === "brouillon" && (
          <button onClick={() => changerStatut("publie")} style={wBtnSm(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>✓ Publier</button>
        )}
        {droits.publier && page.statut === "publie" && (
          <button onClick={() => changerStatut("archive")} style={wGhostSm}>📥 Archiver</button>
        )}
        {droits.modifier && <button onClick={verifierCoherence} disabled={analyse} style={wBtnSm(THEME.purpleDim, THEME.purple, THEME.purple)}>{analyse ? "⏳ …" : "🔎 Cohérence"}</button>}
        <button onClick={dupliquer} style={wGhostSm} title="Repartir de cette page pour en créer une nouvelle">⧉ Dupliquer</button>
        <button onClick={imprimer} style={wGhostSm} title="Version imprimable — enregistre en PDF depuis la fenêtre d'impression">🖨️ PDF</button>
        {droits.supprimer && <button onClick={supprimer} style={wBtnSm(THEME.redDim, THEME.red, THEME.red)}>🗑️</button>}
      </div>

      {/* Accusé de lecture */}
      {/* ZERO_ORPHELIN — `!!` et pas seulement `page.a_lire` : MySQL renvoie
          un TINYINT, donc le NOMBRE 0. En JSX, `0 && …` vaut `0`, et React
          affiche le zéro. Avec `false` il n'afficherait rien. */}
      {!!page.a_lire && d.lecture && (
        <div style={{ background: d.lecture.luParMoi ? THEME.emeraldDim : THEME.amberDim, border: `1px solid ${d.lecture.luParMoi ? THEME.emerald : THEME.amber}`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: d.lecture.luParMoi ? THEME.emerald : THEME.amber, fontSize: 12.5, flex: 1, minWidth: 220 }}>
            {d.lecture.luParMoi
              ? `✓ Tu as validé la lecture de la version ${d.lecture.version}.`
              : `📖 Page à lire — confirme que tu as pris connaissance de la version ${d.lecture.version}.`}
            {" "}
            <b>{d.lecture.lecteurs.length} / {d.lecture.total}</b> du staff à jour.
          </span>
          <button onClick={() => setDetailLecteurs(!detailLecteurs)} style={wGhostSm}>{detailLecteurs ? "Masquer" : "Qui ?"}</button>
          {!d.lecture.luParMoi && (
            <button onClick={marquerLu} style={wBtnSm(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>✓ J'ai lu</button>
          )}
        </div>
      )}

      {/* MARQUE_PAGE_PERSONNEL — sur une fiche qui n'est PAS « à lire », la
          lecture n'engage que toi : pas de compteur, pas de liste, personne
          ne te compte. Mais c'est ce clic qui fait sortir la fiche de la
          bande « Commence par là » de l'accueil. */}
      {d.lecture && !page.a_lire && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                      border: `1px solid ${THEME.border}`, borderRadius: 10, padding: "8px 12px" }}>
          <span style={{ flex: 1, minWidth: 200, fontSize: 12.5,
                         color: d.lecture.luParMoi ? THEME.emerald : THEME.textMuted }}>
            {d.lecture.luParMoi
              ? `✓ Tu as lu la version ${d.lecture.version} de cette fiche.`
              : "Tu n'as pas encore marqué cette fiche comme lue."}
          </span>
          {!d.lecture.luParMoi && (
            <button onClick={marquerLu} style={wBtnSm(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>
              ✓ J'ai lu
            </button>
          )}
        </div>
      )}

      {detailLecteurs && d.lecture && (
        <div style={{ ...wPanel, fontSize: 12.5 }}>
          <div style={{ ...wLbl, marginBottom: 6 }}>À jour sur la version {d.lecture.version}</div>
          <div style={{ color: THEME.emerald, marginBottom: 10 }}>
            {d.lecture.lecteurs.length === 0 ? "personne pour l'instant"
              : d.lecture.lecteurs.map((l) => `${l.nom} (${new Date(l.lu_le).toLocaleDateString("fr")})`).join(" · ")}
          </div>
          <div style={{ ...wLbl, marginBottom: 6 }}>Pas encore lu</div>
          <div style={{ color: THEME.textMuted }}>
            {d.lecture.enRetard.length === 0 ? "tout le monde est à jour 🎉" : d.lecture.enRetard.join(" · ")}
          </div>
        </div>
      )}

      {!!page.ia_assiste && (
        <div style={{ background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, borderRadius: 10, padding: "10px 14px", color: THEME.purple, fontSize: 12.5 }}>
          🤖 Cette page a été rédigée avec l'assistant et n'a pas encore été relue par un humain. Le badge disparaîtra au prochain enregistrement.
        </div>
      )}

      {coherence && (
        <div style={{ ...wPanel, borderColor: coherence.contradictions && coherence.contradictions.length ? THEME.amber : THEME.emerald }}>
          {coherence.erreur ? <div style={{ color: THEME.red, fontSize: 13 }}>{coherence.erreur}</div>
            : coherence.contradictions.length === 0 ? (
              <div style={{ color: THEME.emerald, fontSize: 13 }}>
                ✓ Aucune contradiction repérée avec les {coherence.voisines.length} page(s) les plus proches.
              </div>
            ) : (
              <>
                <div style={{ color: THEME.amber, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
                  ⚠️ {coherence.contradictions.length} contradiction(s) possible(s) — à trancher entre humains
                </div>
                {coherence.contradictions.map((c, i) => (
                  <div key={i} style={{ background: THEME.surface, borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 12.5 }}>
                    <div style={{ color: THEME.text, fontWeight: 700, marginBottom: 4 }}>{c.sujet}</div>
                    <div style={{ color: THEME.textMuted }}>Ici : « {c.dit_ici} »</div>
                    <div style={{ color: THEME.textMuted }}>
                      Dans <span onClick={() => onOuvrir(c.page_source)} style={{ color: THEME.accent, cursor: "pointer" }}>{c.page_source}</span> : « {c.dit_ailleurs} »
                    </div>
                  </div>
                ))}
              </>
            )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 300px", gap: 14, alignItems: "start" }}>
        <div style={wPanel}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <h1 style={{ color: THEME.text, fontSize: 21, fontWeight: 800, margin: 0 }}>{page.titre}</h1>
            {page.statut === "brouillon" && <Badge text="brouillon" bg={THEME.amberDim} color={THEME.amber} />}
            {!!page.verrouille && <Badge text="🔒 verrouillée" bg={THEME.amberDim} color={THEME.amber} />}
          </div>
          <div style={{ color: THEME.textMuted, fontSize: 12.5, marginBottom: 16 }}>
            Modifiée par <b style={{ color: THEME.gold }}>{page.editeur || page.auteur || "—"}</b>{" "}
            le {new Date(page.updated_at).toLocaleString("fr")} · version {page.version}
          </div>

          {toc.length > 3 && (
            <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
              <div style={{ ...wLbl, marginBottom: 6 }}>Sommaire</div>
              {toc.map((t) => (
                <a key={t.ancre} href={`#${t.ancre}`}
                  onClick={(e) => { e.preventDefault(); const el = document.getElementById(t.ancre); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  style={{ display: "block", color: THEME.accent, fontSize: 12.5, textDecoration: "none", padding: "2px 0", paddingLeft: (t.niveau - 1) * 12 }}>
                  {t.texte}
                </a>
              ))}
            </div>
          )}

          <WikiContenu html={html} onChip={chip} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {fichiers.length > 0 && (
            <div style={wPanel}>
              <div style={{ ...wLbl, marginBottom: 10 }}>
                📎 Pièces jointes{fichiers.length > 1 ? ` (${fichiers.length})` : ""}
              </div>
              {fichiers.map((f) => {
                // Deux URL pour le même fichier : l'une l'affiche dans un
                // onglet, l'autre force l'enregistrement. C'est le serveur
                // qui décide, sur le paramètre telecharger.
                const base = `${API_BASE}/api/wiki/fichiers/${f.id}?jeton=${d.jetonFichiers}`;
                const urlOuvrir = base;
                const urlTelecharger = `${base}&telecharger=1`;
                const estImage = /^image\//.test(f.mime || "");
                const estPdf = /pdf/i.test(f.mime || "") || /\.pdf$/i.test(f.nom_origine || "");
                // On n'ouvre dans un onglet que ce qu'un navigateur sait
                // afficher ; pour un .docx ou un .zip, « Ouvrir » ne ferait
                // que déclencher un téléchargement déguisé.
                const consultable = estImage || estPdf || /^text\//.test(f.mime || "");
                const ko = Math.round((f.taille_octets || 0) / 1024);

                return (
                  <div key={f.id} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    {estImage && (
                      <a href={urlOuvrir} target="_blank" rel="noreferrer" style={{ display: "block", marginBottom: 8 }}>
                        <img src={urlOuvrir} alt={f.nom_origine}
                          style={{ width: "100%", maxHeight: 150, objectFit: "cover", borderRadius: 8, display: "block", border: `1px solid ${THEME.border}` }} />
                      </a>
                    )}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16, lineHeight: 1.2 }}>{estImage ? "🖼️" : estPdf ? "📄" : "📎"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div title={f.nom_origine}
                          style={{ color: THEME.text, fontSize: 12.5, fontWeight: 600, wordBreak: "break-word" }}>
                          {f.nom_origine}
                        </div>
                        <div style={{ color: THEME.textDim, fontSize: 10.5, marginTop: 2 }}>
                          {ko >= 1024 ? `${(ko / 1024).toFixed(1)} Mo` : `${ko} Ko`}{f.auteur ? ` · ${f.auteur}` : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <a href={urlTelecharger} target="_blank" rel="noreferrer"
                        style={{ ...wBtnSm(THEME.accentDim, THEME.accent, THEME.accent), textDecoration: "none", display: "inline-block" }}>
                        ⬇ Télécharger
                      </a>
                      {consultable && (
                        <a href={urlOuvrir} target="_blank" rel="noreferrer"
                          style={{ ...wGhostSm, textDecoration: "none", display: "inline-block" }}>
                          Ouvrir
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={wPanel}>
            <div style={{ ...wLbl, marginBottom: 10 }}>Fiche</div>
            {[
              ["Catégorie", <Badge key="c" text={st.label} bg={st.fond} color={st.couleur} />],
              // PAGE_VIS_4
              ["Visibilité", { guide: "Guides et plus", modo: "Modos et plus",
                               admin: "Admins et plus", fondateur: "Fondateurs seuls",
                               staff: "Guides et plus (ancien « staff »)" }[page.visibilite] || page.visibilite],
              ["Auteur", page.auteur || "—"],
              ["Version", page.version],
              ["Vues", page.vues],
              ["Tags", (page.tags || "—")],
            ].map(([k, v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: `1px solid ${THEME.border}`, fontSize: 12.5 }}>
                <span style={{ color: THEME.textMuted }}>{k}</span>
                <span style={{ color: THEME.text, textAlign: "right" }}>{v}</span>
              </div>
            ))}
            {droits.publier && (
              <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                <button onClick={async () => { await api.epingle(page.id, !page.epingle); charger(); }} style={wGhostSm}>
                  {page.epingle ? "📌 Désépingler" : "📌 Épingler"}
                </button>
                <button onClick={async () => { await api.aLire(page.id, !page.a_lire); charger(); }} style={wGhostSm}
                  title="Demander à chaque membre du staff de confirmer qu'il a lu cette page">
                  {page.a_lire ? "📖 Ne plus exiger la lecture" : "📖 Marquer « à lire »"}
                </button>
                {droits.fondateur && (
                  <button onClick={async () => { await api.verrou(page.id, !page.verrouille); charger(); }} style={wGhostSm}>
                    {page.verrouille ? "🔓 Déverrouiller" : "🔒 Verrouiller"}
                  </button>
                )}
              </div>
            )}
          </div>

          {(liens.length > 0 || entrants.length > 0) && (
            <div style={wPanel}>
              {liens.length > 0 && (
                <>
                  <div style={{ ...wLbl, marginBottom: 8 }}>🔗 Liens de la page</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: entrants.length ? 14 : 0 }}>
                    {liens.map((l, i) => (
                      <span key={i} onClick={() => chip(l.cible_type, l.cible_id, l.cible_label)}
                        style={{ ...wBtnSm(THEME.surface, THEME.border, THEME.textMuted), cursor: "pointer" }}>
                        {l.cible_label || `${l.cible_type} ${l.cible_id}`}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {entrants.length > 0 && (
                <>
                  <div style={{ ...wLbl, marginBottom: 8 }}>📥 Pages qui pointent ici</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {entrants.map((p) => (
                      <span key={p.slug} onClick={() => onOuvrir(p.slug)}
                        style={{ ...wBtnSm(THEME.purpleDim, THEME.purple, THEME.purple), cursor: "pointer" }}>📚 {p.titre}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
